import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { UXPContext } from '@core/context/Context';
import ErrorState from '@helper/ErrorState';
import Button from '@widget/buttons/Button';
/** A failed lazy-chunk load (webpack sets error.name; message survives minify).
 *  Usually means a new version deployed and the running page holds stale chunk
 *  URLs — recoverable with a reload, not a fatal error. */
function isChunkLoadError(error) {
    if (!error)
        return false;
    if (error.name === 'ChunkLoadError')
        return true;
    const s = String(error.message || error);
    return /Loading chunk .* failed/i.test(s) || /ChunkLoadError/.test(s);
}
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, isChunkError: false };
    }
    static getDerivedStateFromError(error) {
        // Update state so the next render shows the fallback UI.
        return { hasError: true, isChunkError: isChunkLoadError(error) };
    }
    componentDidCatch(error, errorInfo) {
        // Log error details if needed
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
    render() {
        var _a;
        if (this.state.hasError) {
            // Every string is resolved here and passed to ErrorState, which renders
            // without a UXP context — the fallback must survive a missing provider.
            // Full mode, centred in the parent: ErrorState steps down to inline (then an
            // icon) by itself when the boundary wraps something small, such as a tile.
            const $L = (_a = this.context) === null || _a === void 0 ? void 0 : _a.$L;
            const text = (key, fallback) => ($L ? $L(key) : fallback);
            // A stale/failed lazy chunk is recoverable — offer a reload rather
            // than the fatal message (never auto-reload: protects unsaved work).
            if (this.state.isChunkError) {
                return (_jsx(ErrorState, { mode: "default", variant: "warning", descriptionDisplay: "inline", title: text('uxp-core.text.new-version-available', 'A new version is available.'), action: _jsx(Button, { title: text('uxp-core.text.reload', 'Reload'), icon: "fas rotate-right", onClick: () => window.location.reload() }) }));
            }
            return (_jsx(ErrorState, { mode: "default", variant: "error", descriptionDisplay: "inline", title: text('uxp-core.text.aw-snap', 'Aw, snap!'), description: text('uxp-core.text.something-went-wrong', 'Something went wrong') }));
        }
        return this.props.children;
    }
}
ErrorBoundary.contextType = UXPContext;
export default ErrorBoundary;
